/**
 * The dock's move plumbing: one dispatch that the pointer, the keyboard and the ⋯ menu go through,
 * and one monitor that ends every pointer gesture — including the ones that land on nothing.
 *
 * It decides nothing. `runMoveCommand` gates and applies, `exerciseMove.ts` words the result, and
 * the dock's own live region (the page's, handed in as `announce`) reads it. What it buys is that
 * the three entry points cannot drift: there is one of them.
 *
 * ── Three sources land here, and only one of them starts in this feature ─────────────────────
 * · An exercise block, dragged inside the day — mechanic 2.
 * · An exercise block, dragged onto a WEEKDAY column of the week behind the panel — mechanic 3. The
 *   drop target belongs to `pages/plan-builder`; the payload is this feature's, and the monitor
 *   resolves the weekday back to a day token through `dayTargets`.
 * · A library row, dragged into the list — mechanic 4. The payload is
 *   `features/exercise-library-rail`'s and the target is this feature's. Since 10 August 2026 it may
 *   land in EITHER block: the two dashed zones both receive, and the one that received says whether
 *   the movement is a warm-up or main work.
 * · Since 29 October 2026 an exercise BLOCK may land on the warm-up zone too, which makes it warm-up
 *   (`moveExerciseToWarmup`). That zone is the one drop that decides a role for a block.
 *
 * One monitor for all three rather than one per source, because the ENDING is the same question in
 * every case and because a release over nothing has to be a cancel — view ADR §11 — and only a
 * monitor sees that. A per-target `onDrop` never fires for it.
 */

import { useEffect, useRef, useState } from 'react';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import {
  runMoveCommand,
  useIsForkPlan,
  type AuthorableExerciseRole,
  type ExercisePick,
} from '~/entities/plan-draft';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import {
  crossDayMoveConfirm,
  exerciseMoveAnnouncement,
  type DayMoveTarget,
} from '../model/exerciseMove';
import type { Translate } from '../model/dayProgramming';

/** Where a keyboard lift inside the day list is currently pointed. */
export interface PendingBlockMove {
  /** The block being carried, by the slot token of its first member. */
  readonly blockToken: string;
  /** The block POSITION it is pointed at — not a gap. See `useKeyboardMove`'s header. */
  readonly position: number;
}

/** One move, as `runMoveCommand` takes it. */
interface BlockMove {
  readonly exerciseSlotToken: string;
  readonly targetDaySlotToken: string;
  readonly targetBlockIndex: number;
  readonly name: string;
}

/**
 * A cross-day move held back while the trainer answers for it — see `crossDayMoveConfirm`.
 *
 * The prompt is captured WITH the move rather than re-derived while the dialog stands: between the
 * question and the answer the trainer can undo, rename or delete something else, and a prompt read
 * back off the tree would name whatever now stands in that day.
 */
interface HeldMove {
  readonly move: BlockMove;
  readonly prompt: ConfirmPrompt;
}

export interface ExerciseMoveState {
  readonly pending: PendingBlockMove | null;
  readonly setPending: (pending: PendingBlockMove | null) => void;
  /**
   * Move one exercise block to a position in a day, and say what happened. The ONE write behind all
   * three paths.
   *
   * `targetBlockIndex` is a GAP among the target day's main-work blocks — `0` is above everything,
   * `blockCount` is at the end.
   */
  readonly moveBlock: (
    exerciseSlotToken: string,
    targetDaySlotToken: string,
    targetBlockIndex: number,
    name: string,
  ) => void;
  /**
   * Insert a library pick into a day. Mechanic 4's dispatch.
   *
   * The ROLE comes from the target that received the drop, not from whatever block the rail is
   * currently aimed at: the two dashed zones are two different landings, and a drag that ignored
   * which one it was released over would put a warm-up in the main work.
   *
   * `targetBlockIndex` is the gap for a `Main` insert and is ignored for a `Warmup` one — the
   * warm-up run draws no positions to land between, so that insert appends.
   */
  readonly insertPick: (
    targetDaySlotToken: string,
    targetBlockIndex: number,
    pick: ExercisePick,
    role: AuthorableExerciseRole,
  ) => void;
  /**
   * What a cross-day move on a RUNNING plan asks before it happens, or `null` when nothing is
   * waiting on an answer.
   *
   * It is held here rather than in the card that dispatched it for the reason `useDayActions` gives
   * one layer up: there is exactly one pending move on a screen at a time, and a dialog mounted per
   * card would be one `AlertDialog` root per exercise competing for the same portal and trap. The
   * three paths — pointer, keyboard lift and menu — all go through `moveBlock`, so all three are
   * gated by construction rather than by three callers remembering to ask.
   */
  readonly confirm: ConfirmPrompt | null;
  readonly confirmMove: () => void;
  readonly cancelMove: () => void;
}

export function useExerciseMove(options: {
  readonly t: Translate;
  /** The day the form is open on — the SOURCE of every move dispatched from it. */
  readonly daySlotToken: string;
  readonly dayTargets: readonly DayMoveTarget[];
  readonly announce: (message: string) => void;
}): ExerciseMoveState {
  const [pending, setPending] = useState<PendingBlockMove | null>(null);
  const [held, setHeld] = useState<HeldMove | null>(null);
  const { t, daySlotToken, dayTargets, announce } = options;
  // A running client fork is the only plan whose cross-day move costs anything — see
  // `crossDayMoveConfirm`. A library template has no client downstream of it.
  const isLivePlan = useIsForkPlan();

  const dayLabelOf = (slotToken: string): string =>
    dayTargets.find((target) => target.slotToken === slotToken)?.label ?? '';
  /** The day's bare identity — what the confirm names it by. See `crossDayMoveConfirm`. */
  const dayShortLabelOf = (slotToken: string): string =>
    dayTargets.find((target) => target.slotToken === slotToken)?.shortLabel ?? '';

  /** The write itself, once nothing is left to ask. */
  const dispatchMove = (move: BlockMove): void => {
    const { exerciseSlotToken, targetDaySlotToken, targetBlockIndex, name } = move;
    const outcome = runMoveCommand({
      kind: 'moveExercise',
      exerciseSlotToken,
      targetDaySlotToken,
      targetBlockIndex,
    });
    announce(
      exerciseMoveAnnouncement(t, outcome, {
        name,
        targetDayLabel: dayLabelOf(targetDaySlotToken),
        role: 'Main',
      }),
    );
  };

  const moveBlock = (
    exerciseSlotToken: string,
    targetDaySlotToken: string,
    targetBlockIndex: number,
    name: string,
  ): void => {
    setPending(null);
    const move = { exerciseSlotToken, targetDaySlotToken, targetBlockIndex, name };
    const prompt = crossDayMoveConfirm(t, {
      isLivePlan,
      sourceDaySlotToken: daySlotToken,
      targetDaySlotToken,
      name,
      targetDayLabel: dayShortLabelOf(targetDaySlotToken),
    });
    if (prompt !== null) {
      setHeld({ move, prompt });
      return;
    }
    dispatchMove(move);
  };

  /**
   * A main-work block dropped on the warm-up zone: it becomes warm-up, in its own day. No cross-day
   * confirm — nothing leaves the day, so no slot is re-minted and no progression restarts.
   */
  const moveToWarmup = (exerciseSlotToken: string, name: string): void => {
    setPending(null);
    const outcome = runMoveCommand({ kind: 'moveExerciseToWarmup', exerciseSlotToken });
    announce(
      exerciseMoveAnnouncement(t, outcome, {
        name,
        targetDayLabel: dayLabelOf(daySlotToken),
        role: 'Warmup',
      }),
    );
  };

  const insertPick = (
    targetDaySlotToken: string,
    targetBlockIndex: number,
    pick: ExercisePick,
    role: AuthorableExerciseRole,
  ): void => {
    setPending(null);
    const outcome = runMoveCommand({
      kind: 'insertFromLibrary',
      targetDaySlotToken,
      targetBlockIndex,
      pick,
      role,
    });
    announce(
      exerciseMoveAnnouncement(t, outcome, {
        name: pick.name,
        targetDayLabel: dayLabelOf(targetDaySlotToken),
        role,
      }),
    );
  };

  // The latest handlers, so the monitor binds ONCE. A monitor rebound between `onDragStart` and
  // `onDrop` never sees the drop at all. Written in an EFFECT rather than during render —
  // `react-hooks/refs` — which is safe because the only reader is a drop callback.
  const latest = useRef({ moveBlock, moveToWarmup, insertPick, dayTargets });
  useEffect(() => {
    latest.current = { moveBlock, moveToWarmup, insertPick, dayTargets };
  });

  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) =>
          source.data.kind === 'exercise' || source.data.kind === 'libraryExercise',
        onDrop: ({ source, location }) => {
          setPending(null);
          const target = location.current.dropTargets[0];
          if (target === undefined) return;
          const site = resolveTarget(target.data, latest.current.dayTargets);
          if (site === null) return;

          if (source.data.kind === 'libraryExercise') {
            const pick = source.data.pick;
            if (!isPick(pick)) return;
            latest.current.insertPick(site.daySlotToken, site.blockIndex, pick, site.role);
            return;
          }
          const exerciseSlotToken = source.data.exerciseSlotToken;
          const name = source.data.name;
          if (typeof exerciseSlotToken !== 'string') return;
          // The warm-up zone is the open day's own, and every block dragged here starts in that day,
          // so landing there is a role change in place (owner, 29 October 2026).
          if (site.role === 'Warmup') {
            latest.current.moveToWarmup(exerciseSlotToken, typeof name === 'string' ? name : '');
            return;
          }
          latest.current.moveBlock(
            exerciseSlotToken,
            site.daySlotToken,
            site.blockIndex,
            typeof name === 'string' ? name : '',
          );
        },
      }),
    [],
  );

  return {
    pending,
    setPending,
    moveBlock,
    insertPick,
    confirm: held?.prompt ?? null,
    confirmMove: () => {
      if (held === null) return;
      setHeld(null);
      dispatchMove(held.move);
    },
    cancelMove: () => {
      setHeld(null);
      announce(t('web:planBuilder.dnd.cancelled'));
    },
  };
}

/** A day, a gap in one of its two blocks, and which block that is. */
interface MoveSite {
  readonly daySlotToken: string;
  readonly blockIndex: number;
  readonly role: AuthorableExerciseRole;
}

/**
 * Turn a drop target's payload into a day, a gap and a block.
 *
 * Four shapes reach here. A `blockSlot` carries its own position and the hitbox's closest edge, and
 * a drop on the bottom edge is the gap BELOW it — that is the whole of the edge-to-index conversion,
 * written once. A `blockList` is the run-off area under the last block, and the dashed zone that
 * closes it. A `warmupList` is the dashed zone closing the WARM-UP run, which appends and therefore
 * reports no gap at all. A `weekday` is a column of the week behind the panel, which appends too:
 * the week card shows no positions to land between, and inventing one from a pointer's height over
 * a card that draws none would be a guess.
 */
function resolveTarget(
  data: Record<string | symbol, unknown>,
  dayTargets: readonly DayMoveTarget[],
): MoveSite | null {
  if (data.kind === 'blockSlot') {
    const index = data.blockIndex;
    const daySlotToken = data.daySlotToken;
    if (typeof index !== 'number' || typeof daySlotToken !== 'string') return null;
    // The one place an edge becomes an index: the bottom edge of block N is the gap at N + 1.
    return {
      daySlotToken,
      blockIndex: extractClosestEdge(data) === 'bottom' ? index + 1 : index,
      role: 'Main',
    };
  }
  if (data.kind === 'blockList') {
    const daySlotToken = data.daySlotToken;
    const blockCount = data.blockCount;
    if (typeof daySlotToken !== 'string' || typeof blockCount !== 'number') return null;
    return { daySlotToken, blockIndex: blockCount, role: 'Main' };
  }
  if (data.kind === 'warmupList') {
    const daySlotToken = data.daySlotToken;
    if (typeof daySlotToken !== 'string') return null;
    // The index is ignored for a warm-up insert — `insertFromLibrary` appends — and 0 is the only
    // honest stand-in: this drop was never given a position to report.
    return { daySlotToken, blockIndex: 0, role: 'Warmup' };
  }
  if (data.kind === 'weekday') {
    const weekday = data.weekday;
    if (typeof weekday !== 'number') return null;
    const day = dayTargets.find((target) => target.weekday === weekday);
    // A rest day has no session to receive an exercise. Refusing here rather than creating a day is
    // deliberate: "+ Trening" is the act that creates one, and a drag that silently minted a day
    // would put a workout on the client's calendar as a side effect of a mis-aimed gesture.
    return day === undefined
      ? null
      : { daySlotToken: day.slotToken, blockIndex: day.blockCount, role: 'Main' };
  }
  return null;
}

function isPick(value: unknown): value is ExercisePick {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<ExercisePick>;
  return typeof candidate.id === 'number' && typeof candidate.name === 'string';
}
